
import Vue from 'vue';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { Component } from 'vue-property-decorator';
import Toasted from 'vue-toasted';
import { handleError, initClient } from '../../api';
import { fixISettings, WindowType } from '../../util';
import {
  Cell,
  CellClient,
  CellList,
  CellSettings,
  CostDto,
  CostSumup,
  ICostDto,
  ICostSumup,
  Inventory,
  InventoryUiClient,
  Line,
  LineClient,
  SettingsApiControllerUIClient,
} from '../backend/backendapi';
import OSXTreeView from '../osxtreeview/osxtreeview.controller';
import { CellTreeCollapsable, ICellTreeCollapsable, OsxTreeElement } from './cell.model';
import { mapTree } from './cell.util';
import MoveModal from './modal/movemodal/movemodal.controller';
import EditModal from './modal/editmodal/EditModal';
import PrintModal from './modal/PrintModal';
import DropZone from '../dropzone/DropZone';
import GroupModal from "./modal/groupmodal/GroupModal";


declare var window: WindowType;

type CellViewType = 'vuegoodtable' | 'osxtreeview';
type SortType = 'asc' | 'desc' | null;

Vue.use(Toasted, { duration: 2000 });

@Component({
  components: {
    DropZone,
    EditModal,
    GroupModal,
    PrintModal,
    VueGoodTable,
    ImageViewer: require('../imageviewer/imageviewer.vue.html').default,
    MoveModal: require('./modal/movemodal/movemodal.vue.html').default,
    OsxTreeView: require('../osxtreeview/osxtreeview.vue.html').default,
  },
})
export default class CellUi extends Vue {
  readonly cellsList: CellList[] = [];
  readonly CellsTree: ICellTreeCollapsable[] = [];
  readonly selectedTreeCollapsable: ICellTreeCollapsable[] = [];
  editCell: Cell | null = null;
  readonly lines: Line[] = [];
  readonly inventories: Inventory[] = [];
  settings: CellSettings | null = null;
  cellviewUpdateTriggerWorkaround = false;
  sortUpdateTriggerWorkaround = false;

  readonly lineAPI = initClient(LineClient);
  readonly cellAPI = initClient(CellClient);
  readonly inventoryAPI = initClient(InventoryUiClient);
  readonly settingsApi = initClient(SettingsApiControllerUIClient);

  get canCellEdit(): boolean {
    return window.application.permissions.duty.isDutyCellEdit;
  }
  
  get columnsConfig(): any[] {
    return [
      {
        label: 'Name',
        field: (e: ICellTreeCollapsable) => e.item.name,
      },
      {
        label: this.$i18n.t('txtIdent').toString(),
        field: (e: ICellTreeCollapsable) => e.item.ident,
      },
      {
        label: this.$i18n.t('txtDescription').toString(),
        field: (e: ICellTreeCollapsable) => e.item.description,
      },
      {
        label: this.$i18n.t('txtSupplier').toString(),
        field: (e: ICellTreeCollapsable) => e.item.supplier,
      },
    ];
  }
  
  get isMap(): boolean {
    return window.application.check2.isMap;
  }

  get isUpload(): boolean {
    return window.application.permissions.duty.isCellUpload;
  };

  get selectConfig(): object {
    return {
      enabled: true,
      selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
      selectionInfoClass: 'custom-class',
      selectionText: this.$i18n.t('comSelectionCnt').toString(),
      clearSelectionText: this.$i18n.t('comClear').toString(),
      disableSelectInfo: false, // disable the select info panel on top
      selectAllByGroup: true, // when used in combination with a grouped table, add a checkbox in the header row to check/uncheck the entire group
    };
  }

  get tableData(): ICellTreeCollapsable[] {
    return this.sortedCellsTree.map(e => e.flatMap()).reduce((l, r) => l.concat(r), []);
  }

  get osxData(): OsxTreeElement[] {
    return this.sortedCellsTree.map(e => new OsxTreeElement(e, null));
  }

  get allowMediaDelete(): boolean {
    return window.application.permissions.cell.mediaDelete;
  }

  get selectedCellList(): CellList[] {
    return this.selectedTreeCollapsable.map(e => e.item);
  }

  get sortedCellsTree(): ICellTreeCollapsable[] {
    this.sortUpdateTriggerWorkaround;
    switch (this.settings?.sort) {
      case 'asc':
        return [...this.CellsTree].map(e => e.deepSorted(false)).sort((l, r) => (l.item.name ?? '').localeCompare(r.item.name ?? ''));
      case 'desc':
        return [...this.CellsTree].map(e => e.deepSorted(true)).sort((l, r) => -(l.item.name ?? '').localeCompare(r.item.name ?? ''));
      default:
        return this.CellsTree;
    }
  }

  get cellview(): CellViewType {
    this.cellviewUpdateTriggerWorkaround;
    return this.settings ? this.settings.cellView as CellViewType : 'vuegoodtable';
  }

  set cellview(val: CellViewType) {
    this.cellviewUpdateTriggerWorkaround = !this.cellviewUpdateTriggerWorkaround;
    if (!this.settings) {
      return;
    }
    this.settings.cellView = val;
    this.clearSelection();
    this.saveSettings();
  }

  get sort(): SortType {
    this.sortUpdateTriggerWorkaround;
    return this.settings ? this.settings.sort as SortType : null;
  }

  set sort(val: SortType) {
    this.sortUpdateTriggerWorkaround = !this.sortUpdateTriggerWorkaround;
    if (!this.settings) {
      return;
    }
    this.settings.sort = val ?? undefined;
    this.clearSelection();
    this.saveSettings();
  }

  get url(): string {
    return window.application.baseUrl + 'api/cell/import';
  }

  get urlPivot(): string {
    return window.application.baseUrl + 'pivot';
  }

  constructor() {
    super();
    //fix model serialization
    ICostSumup.fromJS = CostSumup.fromJS;
    ICostDto.fromJS = CostDto.fromJS;
    fixISettings();
  }

  async mounted(): Promise<void> {
    await Promise.all([
      this.fetchLines(),
      this.fetchCells(),
      this.fetchInventories(),
      this.loadSettings(),
    ]);
    this.handleModalOpenTriggerByUrl();
  }

  fetchLines(): Promise<void> {
    return this.lineAPI.getLinesData()
        .then(data => {
          this.lines.splice(0);
          this.lines.push(...data);
        });
  }

  fetchCells(): Promise<void> {
    //save collapsed state
    let colMap = new Map<number, boolean>();
    this.traverseTree((ct: ICellTreeCollapsable) => colMap.set(ct.item.id!, ct.collapsed));

    //fetch
    return this.cellAPI.getCellsAndTreeData()
        .then(data => {
          this.cellsList.splice(0);
          this.CellsTree.splice(0);
          this.cellsList.push(...data);
          this.CellsTree.push(...mapTree(data).map(e => new CellTreeCollapsable(e)));

          //set collapsed state
          if (colMap.size === 0) {//first load => collapse everything except top layer
            this.traverseTree((ct: ICellTreeCollapsable) => ct.collapsed = ct.item.level! > 0);
          } else {
            this.traverseTree((ct: ICellTreeCollapsable) => ct.collapsed = colMap.get(ct.item.id!) ?? false);
          }
        });
  }

  fetchInventories(): Promise<void> {
    return this.inventoryAPI.getData()
        .then(data => {
          this.inventories.splice(0);
          this.inventories.push(...data);
        });
  }

  fetchEditCell(id: number): Promise<Cell> {
    return this.cellAPI.getCellData(id);
  }

  async loadSettings(): Promise<void> {
    const set = (await this.settingsApi.get('CellSettings', false, false)).result!;
    this.settings = new CellSettings();
    this.settings.init(set);
  }

  async saveSettings(): Promise<void> {
    if (!this.settings) {
      return;
    }
    await this.settingsApi.update('CellSettings', false, false, JSON.stringify(this.settings))
        .catch(e => this.$toasted.error(this.$t('txtError') + ': ' + JSON.stringify(e)));
  }

  handleModalOpenTriggerByUrl(): void {
    let url = new URL(window.location.href);
    if (!url.searchParams.has('cell')) {
      return;
    }
    let id = parseInt(url.searchParams.get('cell')!);
    let cell = this.cellsList.find(e => e.id === id);
    if (cell) {
      this.editExistingCell(cell);

      //remove the search param from url to a reload of this component does not trigger a the modal again
      url.searchParams.delete('cell');
      window.history.pushState(undefined, '', url);
    }
  }

  async onMoveStart(): Promise<void> {
    if (this.selectedTreeCollapsable.length === 0) return;
    let fetched = await this.fetchEditCell(this.selectedTreeCollapsable[0].item.id!);

    (this.$refs.moveModal as MoveModal).open(fetched);
  }

  onEdit(): void {
    if (this.selectedTreeCollapsable.length === 0) return;
    this.editExistingCell(this.selectedTreeCollapsable[0].item);
  }

  deleteSelected(): void {
    if (this.selectedTreeCollapsable.length === 0) {
      return;
    }
    this.cellAPI.deleteCellData(this.selectedTreeCollapsable.map(e => e.item.id!))
        .then(_ => this.$toasted.info(this.$t('txtDeleted').toString()))
        .catch(e => handleError(this.$toasted, this.$i18n, e))
        .finally(() => this.fetchAndClear());
  }

  clearSelection(): void {
    (this.$refs.table as VueGoodTable)?.unselectAllInternal();
    (this.$refs.treeview as OSXTreeView)?.clearSelection();
    this.selectedTreeCollapsable.splice(0);
  }

  // Fetches the data after 1 second and also clears the inputs etc.s
  fetchAndClear(): void {
    // Create a new Promise and resolve after 1 seconds
    var myTimerPromise = new Promise((resolve, reject) => {
      setTimeout(resolve, 500);  // "resolve" is already a function, no need for another anonymous function here
    });

    // Fetch data now, within Promise.all()
    Promise.all([myTimerPromise]).then(() => {
      this.fetchCells();
      this.fetchLines();
      this.clearSelection();
      this.editCell = null;
    });
  }

  toggleCollapsed(id: number): void {
    this.traverseTree((ct: ICellTreeCollapsable) => {
      if (ct.item.id === id) {
        ct.collapsed = !ct.collapsed;
      }
    });

    //force refresh
    let t = [...this.CellsTree];
    this.CellsTree.splice(0);
    this.CellsTree.push(...t);
  }

  cycleSort(): void {
    const map = { 'null': 'asc', 'asc': 'desc', 'desc': null };
    console.log(this.sort, map['' + (this.sort ?? null)] ?? null);
    this.sort = map['' + (this.sort ?? null)] ?? null; // coalesce to null in case index not found which results in undefined
  }

  //vgt events
  selectionChanged(params): void {
    this.selectedTreeCollapsable.splice(0);
    this.selectedTreeCollapsable.push(...params.selectedRows);
  }

  onDoubleClickOnCell(item: ICellTreeCollapsable): void {
    this.editExistingCell(item.item);
  }

  //END vgt events

  //osx tree view events
  onOsxSelected(item: OsxTreeElement | null) {
    this.selectedTreeCollapsable.splice(0);
    if (item) {
      this.selectedTreeCollapsable.push(item.item);
    }
  }

  onOsxDoubleClick(item: OsxTreeElement | null) {
    if (!item) {
      return;
    }
    this.onDoubleClickOnCell(item.item);
  }

  //END osx tree view events

  redirectMap() {
    window.location.replace(window.application.baseUrl + 'quick/map');
  }

  createNewCell() {
    let cell = new Cell();
    cell.init({});
    let parentId = this.selectedTreeCollapsable.length > 0 ? (this.selectedTreeCollapsable[0].item.id ?? null) : null;
    (this.$refs.editModal as EditModal).open(cell, parentId);
  }

  async editExistingCell(item: CellList): Promise<void> {
    let fetched = await this.fetchEditCell(item.id!);
    (this.$refs.editModal as EditModal).open(fetched, null);
  }

  traverseTree(fn: (ct: ICellTreeCollapsable) => void): void {
    let s = (ct: ICellTreeCollapsable) => {
      fn(ct);
      ct.children.forEach(s);
    };
    this.CellsTree.forEach(s);
  }
}
